import { useEffect, useState } from "react";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";

interface SiteSearchRow {
  id: number;
  title: string;
  content: string;
  path: string;
  section?: string;
  keywords?: string;
  source_type?: "manual" | "product" | "subcategory" | "category" | "service" | "page";
  source_id?: number | null;
  status: "active" | "inactive";
  created_at?: string;
  updated_at?: string;
}

export default function SiteSearch() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { showModal } = useMessageModal();

  const [data, setData] = useState<SiteSearchRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<SiteSearchRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<SiteSearchRow | null>(null);

  const fetchSiteSearch = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/site-search`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        const freshData: SiteSearchRow[] = result.data || [];

        setData(freshData);

        setSelectedRow((prev) =>
          prev ? freshData.find((item) => item.id === prev.id) || null : null,
        );

        setActiveRow((prev) =>
          prev ? freshData.find((item) => item.id === prev.id) || null : null,
        );
      }
    } catch (error) {
      console.error("Fetch Site Search Error:", error);
    }
  };

  useEffect(() => {
    fetchSiteSearch();
  }, []);

  const handleSubmit = async (formData: Partial<SiteSearchRow>) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";
      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/site-search`
          : `${BASE_URL}/admin/site-search/${activeRow?.id}`;

      const payload = {
        ...formData,
        source_id:
           formData.source_id === undefined
            ? null
            : Number(formData.source_id),
      };

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        await fetchSiteSearch();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Site search record created successfully."
              : "Site search record updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Unable to save site search record.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Site Search Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving site search record.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: SiteSearchRow) => {
    showModal({
      type: "confirm",
      title: "Delete Site Search",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/site-search/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            await fetchSiteSearch();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Site search record deleted successfully.",
              confirmText: "OK",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Unable to delete site search record.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Site Search Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting site search record.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  const searchFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },
    { key: "title", label: "Title", type: "text", colSpan: 2 },
    { key: "content", label: "Content", type: "textarea", colSpan: 2 },
    { key: "path", label: "Path", type: "text", colSpan: 2 },
    { key: "section", label: "Section", type: "text", colSpan: 2 },
    {
      key: "keywords",
      label: "Keywords",
      type: "textarea",
      colSpan: 2,
    },
    {
      key: "source_type",
      label: "Source Type",
      type: "select",
      options: [
        { label: "Manual", value: "manual" },
        { label: "Product", value: "product" },
        { label: "Category", value: "category" },
        { label: "Subcategory", value: "subcategory" },
        { label: "Service", value: "service" },
        { label: "Page", value: "page" },
      ],
      colSpan: 2,
    },
    {
      key: "source_id",
      label: "Source ID",
      type: "number",
      colSpan: 2,
    },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      colSpan: 2,
    },
  ];

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "title", label: "Title" },
    { key: "path", label: "Path" },
    { key: "section", label: "Section" },
    { key: "source_type", label: "Source Type" },
    {
      key: "status",
      label: "Status",
      render: (row: SiteSearchRow) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Site Search"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="site_search_index"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id || null}
            onRowSelect={(row) => setSelectedRow(row)}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Site Search"
        fields={searchFields}
        rowData={activeRow}
        onClose={() => {
          setModalOpen(false);
          setActiveRow(null);
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
}